/**
 * Search utility functions for preprocessing queries and handling stopwords.
 *
 * These utilities help reduce noise in search results by filtering out
 * high-frequency, low-value words (stopwords) before sending queries to the database.
 */

/**
 * List of Dutch juridical stopwords - high-frequency words with low search value.
 * These words appear in most legal documents but don't help narrow down search results.
 *
 * Categories:
 * 1. Prepositions and conjunctions (voorzetsels en voegwoorden)
 * 2. Common legal document words
 * 3. Procedural terms that appear in every case
 *
 * Note: This list was initially based on domain knowledge and should be refined
 * using frequency analysis from the database:
 *
 * SELECT word, ndoc,
 *        ROUND(ndoc::numeric / (SELECT COUNT(*) FROM jurisprudentie) * 100, 1) as doc_percentage
 * FROM ts_stat('SELECT zoek_vector FROM jurisprudentie')
 * ORDER BY ndoc DESC
 * LIMIT 100;
 */
export const JURIDISCHE_STOPWORDS: readonly string[] = [
  // Prepositions and conjunctions (voorzetsels en voegwoorden)
  'wegens',
  'vanwege',
  'betreffende',
  'inzake',
  'omtrent',
  'aangaande',
  'tegen',
  'tussen',
  'onder',
  'naar',
  'vanaf',
  'binnen',
  'buiten',
  'tijdens',
  'gedurende',
  'omstreeks',
  'krachtens',
  'ingevolge',
  'middels',
  'namens',
  'jegens',
  'blijkens',
  'overeenkomstig',

  // Common legal document structure words
  'artikel',
  'lid',
  'sub',
  'jo',     // juncto
  'ex',     // ex artikel
  'bw',     // Burgerlijk Wetboek (when standalone)

  // Procedural terms (appear in every case)
  'zaak',
  'zaaknummer',
  'partijen',
  'eiser',
  'eiseres',
  'gedaagde',
  'appellant',
  'geïntimeerde',
  'verweerder',
  'verweerster',
  'verzoeker',
  'verzoekster',

  // Common verbs and auxiliary words
  'heeft',
  'hebben',
  'worden',
  'wordt',
  'werd',
  'werden',
  'zijn',
  'zal',
  'zullen',
  'zou',
  'zouden',
  'kan',
  'kunnen',
  'kon',
  'konden',
  'moet',
  'moeten',
  'mag',
  'mogen',

  // Articles and pronouns
  'deze',
  'dit',
  'die',
  'dat',
  'welke',
  'welk',
  'waarbij',
  'waardoor',
  'waarvan',
  'waarin',
  'waarmee',
  'waarop',
  'waartoe',

  // Generic legal terms
  'voorts',
  'derhalve',
  'mitsdien',
  'aldus',
  'thans',
  'alsdan',
  'alsnog',
  'evenwel',
  'echter',
  'niettemin',
  'desalniettemin',
] as const;

// Create a Set for O(1) lookup performance
const stopwordsSet = new Set(JURIDISCHE_STOPWORDS.map(w => w.toLowerCase()));

/**
 * Checks if a word is a stopword.
 *
 * @param word - The word to check
 * @returns true if the word is a stopword
 */
export function isStopword(word: string): boolean {
  return stopwordsSet.has(word.toLowerCase());
}

/**
 * Preprocesses a search query by removing stopwords.
 *
 * This function:
 * 1. Splits the query into words
 * 2. Removes stopwords that don't contribute to search relevance
 * 3. Preserves quoted phrases (exact match searches)
 * 4. Preserves special syntax like -word (exclusion) and OR
 *
 * @param query - The original search query
 * @returns The preprocessed query with stopwords removed
 *
 * @example
 * preprocessSearchQuery("ontslag wegens disfunctioneren")
 * // Returns: "ontslag disfunctioneren"
 *
 * @example
 * preprocessSearchQuery('"kennelijk onredelijk" wegens')
 * // Returns: '"kennelijk onredelijk"' (quoted phrase preserved)
 */
export function preprocessSearchQuery(query: string): string {
  if (!query || query.trim() === '') {
    return '';
  }

  // Regex to match quoted phrases
  const quotedPhraseRegex = /"[^"]+"/g;

  // Extract and preserve quoted phrases
  const quotedPhrases: string[] = [];
  const queryWithoutQuotes = query.replace(quotedPhraseRegex, (match) => {
    quotedPhrases.push(match);
    return `__QUOTED_${quotedPhrases.length - 1}__`;
  });

  // Split by whitespace and filter
  const words = queryWithoutQuotes.split(/\s+/);

  const filteredWords = words.filter(word => {
    // Preserve placeholder for quoted phrases
    if (word.startsWith('__QUOTED_')) {
      return true;
    }

    // Preserve special syntax: -word (exclusion), OR operator
    if (word.startsWith('-') || word.toUpperCase() === 'OR') {
      return true;
    }

    // Remove if it's a stopword
    const cleanWord = word.replace(/[^a-zA-ZÀ-ÿ]/g, '').toLowerCase();
    if (cleanWord.length > 0 && isStopword(cleanWord)) {
      return false;
    }

    // Keep the word
    return true;
  });

  // Restore quoted phrases
  let result = filteredWords.join(' ');
  quotedPhrases.forEach((phrase, index) => {
    result = result.replace(`__QUOTED_${index}__`, phrase);
  });

  return result.trim();
}
